import { Trans } from '@lingui/react'
import classNames from 'clsx'
import { bool, number, shape, string } from 'prop-types'
import { PureComponent } from 'react'
import { connectStateResults } from 'react-instantsearch-dom'
import { compose } from 'redux'

import { LANGUAGE_FR, TENANT_CH, TENANT_FR } from 'shared/consts'
import ClearRefinementsSide from 'shared/experiments/components/ClearRefinements'
import CategoryList from 'shared/experiments/components/NewSerp/CategoryList'
import {
  EXTRA_FILTERS_NUMBER,
  FILTER_FLYOUT_CLOSE,
  FILTER_FLYOUT_OPEN,
  FILTER_ID,
} from 'shared/experiments/components/NewSerp/consts'
import FlyoutSortBy from 'shared/experiments/components/NewSerp/FlyoutSortBy'
import {
  getDisplayedFiltersNames,
  getRefinedShippingOptionsCount,
  handleSearchingStateInDom,
  hasActiveHiddenFilters,
  isCategoriesFilterRefined,
  shouldDisplayRatingsFilter,
  shouldDisplayShippingOptions,
} from 'shared/experiments/components/NewSerp/helpers'
import PrescriptionTypes from 'shared/experiments/components/NewSerp/PrescriptionTypes'
import RangeFilter from 'shared/experiments/components/NewSerp/RangeFilter'
import RefinementList from 'shared/experiments/components/NewSerp/RefinementList'
import ShippingOptions from 'shared/experiments/components/NewSerp/ShippingOptions'
import StarRatingStacked from 'shared/experiments/components/NewSerp/StarRatingStacked'
import { EXPERIMENT_VARIATION, SERP_NOW_PUSH } from 'shared/experiments/consts'
import { getExperiment } from 'shared/experiments/utils/experiments'
import { subscribe } from 'shared/store'
import {
  getAverageRatingRangeRefinement,
  getDefaultRange,
  getFiltersOrder,
  getRangeFromResults,
  getSortingItems,
  isAnyFilterAdjusted,
} from 'shared/utils/algolia/filters'
import { updateGTM } from 'shared/utils/gtmUtils'
import { withI18n } from 'shared/utils/withI18n'
import { currencyToSymbol } from 'views/components/atoms/Price/helpers'
import ButtonAsLink from 'views/components/molecules/ButtonAsLink'
import ModalShowButton from 'views/components/molecules/ModalShowButton'
import {
  FACETS_LIMIT,
  FLYOUT_ID,
  PRICE_DEFAULT_VALUES,
} from 'views/components/organisms/AlgoliaInstantSearch/ProductFilter/consts'
import DynamicFilters from 'views/components/organisms/AlgoliaInstantSearch/ProductFilter/DynamicFilters'
import { shouldBeShippedToday } from 'views/components/organisms/NowCountdown/helpers'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { i18nType, publicRuntimeConfigType } from 'views/utils/propTypes'
import 'views/components/organisms/AlgoliaInstantSearch/ProductFilter/ProductFilter.scss'
import './ProductFilterVariant.scss'

/**
 * @see app/views/components/organisms/AlgoliaInstantSearch/ProductFilter/index.js Original component
 */
class ProductFilterVariant extends PureComponent {
  state = this.getInitialState()
  subscriptions = []
  sortingItems = []

  constructor(props) {
    super(props)

    const { indexName, i18n } = props
    this.sortingItems = getSortingItems({
      indexName,
      i18n,
      isSearchResultsPage: true,
    })

    this.subscriptions.push(
      subscribe.after(FILTER_FLYOUT_CLOSE, () => {
        this.setState({ showOverlay: false })
      })
    )
    this.subscriptions.push(
      subscribe.after(FILTER_FLYOUT_OPEN, () => {
        this.setState({ showOverlay: true })
      })
    )
  }

  componentDidUpdate(prevProps) {
    handleSearchingStateInDom({ isSearching: this.props.searching })

    if (prevProps.searchResults.queryID !== this.props.searchResults.queryID) {
      const { searchState } = this.props
      /** @type {ResultsStateReact} */
      const { searchResults } = this.props

      updateGTM(searchState, searchResults)

      // Ignored because it is fine since we are checking queryID
      this.setState(prevState => ({
        ...this.getInitialState(),
        showOverlay: prevState.showOverlay,
        shouldCropFilters: prevState.shouldCropFilters,
      }))
    }
  }

  componentWillUnmount() {
    this.subscriptions.forEach(unsub => unsub())
  }

  getInitialState() {
    const {
      displayedFiltersNumber,
      searchResults = {},
      searchState = {},
      searchState: { refinementList } = {},
      publicRuntimeConfig: {
        publicConfig: { tenant, currency, language },
        pageProperties: { experiments },
      },
    } = this.props
    const displayedFiltersNames = getDisplayedFiltersNames(searchResults)
    const hiddenFiltersNumber = Math.max(
      EXTRA_FILTERS_NUMBER +
        displayedFiltersNames.size -
        displayedFiltersNumber,
      0
    )
    const hasCategoriesFilterRefined = isCategoriesFilterRefined(searchState)
    const shippingOptionsCount = getRefinedShippingOptionsCount(searchState)
    const hasShippingOptionsRefined = Boolean(shippingOptionsCount)
    const shouldStackBottomControls =
      tenant === TENANT_FR || (tenant === TENANT_CH && language === LANGUAGE_FR)
    // CRO-121 code
    const nowFilteringExperiment = getExperiment(SERP_NOW_PUSH, experiments)

    const isNowPushEnabled =
      nowFilteringExperiment.isEnabled &&
      nowFilteringExperiment.variant === EXPERIMENT_VARIATION.V1 &&
      shouldBeShippedToday()

    return {
      hiddenFiltersNumber,
      hasShippingOptionsRefined,
      shippingOptionsCount,
      shouldCropFilters: [
        Boolean(hiddenFiltersNumber),
        !hasShippingOptionsRefined,
        !hasCategoriesFilterRefined,
        !hasActiveHiddenFilters(refinementList),
      ].every(Boolean),
      hasAnyFilterAdjusted: [
        hasCategoriesFilterRefined,
        hasShippingOptionsRefined,
        isAnyFilterAdjusted(searchState),
      ].some(Boolean),
      showOverlay: false,
      shouldStackBottomControls,
      isNowPushEnabled,
      filtersOrder: getFiltersOrder(searchResults),
      currency,
      tenant,
    }
  }

  getDefaultValues = facetName => {
    const { searchState, searchResults } = this.props

    if (searchState && searchResults) {
      const range = getDefaultRange(searchState, searchResults, facetName)

      if (range) {
        return range
      }
    }

    return {
      min: PRICE_DEFAULT_VALUES.min,
      max: PRICE_DEFAULT_VALUES.max,
    }
  }

  getRangeValues = facetName => {
    const { searchResults } = this.props
    if (searchResults) {
      const range = getRangeFromResults(searchResults, facetName)

      if (range) {
        return range
      }
    }

    return {
      min: PRICE_DEFAULT_VALUES.min,
      max: PRICE_DEFAULT_VALUES.max,
    }
  }

  render() {
    const {
      i18n,
      searchState,
      searching,
      indexName,
      searchResults = {},
      searchState: { refinementList },
    } = this.props

    const {
      showOverlay,
      shouldCropFilters,
      shippingOptionsCount,
      hasShippingOptionsRefined,
      hasAnyFilterAdjusted,
      shouldStackBottomControls,
      filtersOrder,
      currency,
      tenant,
    } = this.state

    const averageRating = getAverageRatingRangeRefinement(searchState)
    const labelSortBy = /*i18n*/ 'general.headline.sort'

    return (
      <div className="e-ProductFilterVariant u-padding u-background-color--greySky u-border-radius u-hidden@small-tiny">
        <div
          className={classNames(
            'e-FlyoutFilter__overlay e-FlyoutFilter__overlay--transparent',
            {
              'e-FlyoutFilter__overlay--searchInProgress': searching,
            }
          )}
          id={FLYOUT_ID}
          aria-hidden={showOverlay ? null : 'true'}
        />

        <h5 className="a-h3 u-margin--bottom u-hidden@small-tiny e-ProductFilterVariant__header">
          <Trans id="general.label.filterSearch" />
        </h5>

        <div className="u-padding--bottom u-hidden@medium">
          <ModalShowButton name={`modal-open-${FILTER_ID}`} modalId={FILTER_ID}>
            <Trans id="general.label.filterSearch" />
          </ModalShowButton>
        </div>

        <div
          className={classNames(
            'e-ProductFilterVariant--filters l-flex l-flex--wrap u-padding--sides@small-tiny',
            {
              'e-ProductFilterVariant--filtersCropped': shouldCropFilters,
              'e-ProductFilterVariant--searchInProgress': searching,
            }
          )}
        >
          <FlyoutSortBy
            order={filtersOrder}
            label={i18n._(labelSortBy)}
            indexName={indexName}
            items={this.sortingItems}
          />
          <DynamicFilters order={filtersOrder}>
            {/**
             * Note: every child should have an `atttibute` or `data-attribute` prop
             * in order to be dynamically sorted
             */}

            <ShippingOptions
              label={i18n._('general.label.shippingOptions')}
              attribute="shippingOptions"
              hidden={!shouldDisplayShippingOptions(searchResults)}
              active={hasShippingOptionsRefined}
              count={shippingOptionsCount}
            />

            <RefinementList
              label={i18n._('general.label.filter.pharmaceuticalForm')}
              attribute="pharmaForm"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RangeFilter
              tenant={tenant}
              label={i18n._('general.label.sort.price')}
              delimiterLabel={i18n._('general.label.until')}
              attribute="price"
              name="productFilter__rangeInput"
              rangeValues={this.getRangeValues('price')}
              defaultValues={this.getDefaultValues('price')}
              symbol={currencyToSymbol(currency)}
            />
            <StarRatingStacked
              label={i18n._('ratingOverview.headline')}
              attribute="averageRating"
              name="productFilter__ratingSlider"
              delimiterLabel={i18n._('general.label.until')}
              defaultValues={averageRating}
              precision={1}
              hidden={!shouldDisplayRatingsFilter(searchResults)}
            />
            <RefinementList
              label={i18n._('general.label.quantity')}
              attribute="packSize"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.brand')}
              attribute="brandIntern"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <CategoryList
              label={i18n._('general.label.categories')}
              attribute="primaryCategory"
              limit={FACETS_LIMIT}
            />
            <RefinementList
              label={i18n._('general.label.filter.substances')}
              attribute="activeSubstances"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <PrescriptionTypes
              attribute="prescriptionMedicine"
              label={i18n._('prescription.labels.prescriptionType')}
              trueOptionLabel={i18n._(
                'prescription.labels.prescriptionRequired'
              )}
              falseOptionLabel={i18n._(
                'prescription.labels.noPrescriptionRequired'
              )}
              searchStateRefinementList={refinementList}
            />
            <RefinementList
              label={i18n._('general.label.manufacturer')}
              attribute="manufacturer"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.filter.attributes')}
              attribute="filterAttributes"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.potency')}
              attribute="potency"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.hairtype')}
              attribute="hair_type_multi"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.skintype')}
              attribute="skin_type_multi"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.uv_protection')}
              attribute="uv_protection"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.lens_types')}
              attribute="lens_types"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.absorbency')}
              attribute="absorbency"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.filter.applicationAreas')}
              attribute="application_areas"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.voltage')}
              attribute="voltage"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.animalSpecies')}
              attribute="animal_species"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.language')}
              attribute="book_language"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.author')}
              attribute="authors"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.schuesslerSalts')}
              attribute="schuessler_salts"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.filter.certificates')}
              attribute="certificates"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              attribute="recommended_age"
              label={i18n._('general.label.age')}
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              attribute="hair_color"
              label={i18n._('general.label.hairColor')}
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              attribute="hair_length"
              label={i18n._('general.label.hairLength')}
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              attribute="haircut"
              label={i18n._('general.label.haircut')}
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              attribute="head_circumference"
              label={i18n._('general.label.headCircumference')}
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              attribute="publisher"
              label={i18n._('general.label.publisher')}
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />

            <RefinementList
              label={i18n._('general.label.lifeStage')}
              attribute="life_stage"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.variety')}
              attribute="variety"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.specialNeeds')}
              attribute="special_needs"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.filter.divisibility')}
              attribute="divisibility"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.filter.sport')}
              attribute="sports"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RangeFilter
              tenant={tenant}
              label={i18n._('general.label.filter.discount')}
              delimiterLabel={i18n._('general.label.until')}
              attribute="discountInPercent"
              name="productFilter__discountRangeInput"
              rangeValues={this.getRangeValues('discountInPercent')}
              defaultValues={this.getDefaultValues('discountInPercent')}
              symbol="%"
            />
            <RefinementList
              label={i18n._('general.label.filter.texture')}
              attribute="texture"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.filter.productType')}
              attribute="product_type_filter"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.filter.minerals')}
              attribute="mineral_and_trace_elements"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
            <RefinementList
              label={i18n._('general.label.filter.vitamins')}
              attribute="vitamins"
              limit={FACETS_LIMIT}
              searchStateRefinementList={refinementList}
              search
            />
          </DynamicFilters>
          <div
            className={classNames(
              'e-ProductFilterVariant__bottomControlsWrapper',
              {
                'e-ProductFilterVariant__bottomControlsWrapper--stacked':
                  shouldStackBottomControls,
              }
            )}
          >
            <div className="e-ProductFilterVariant__filterButtonsWrapper l-flex l-flex--wrap u-margin--bottom u-margin-small--top">
              <ButtonAsLink
                className={classNames(
                  'e-ProductFilterVariant__filterButtonUnFold',
                  {
                    'u-hidden': !shouldCropFilters,
                  }
                )}
                onClick={() => this.setState({ shouldCropFilters: false })}
                name="product-filters-unfold"
                inline
              >
                <span className="u-font-weight--bold e-ProductFilterVariant__filterButtonUnFold--text">
                  <Trans id="searchResults.label.showMore" />
                </span>
              </ButtonAsLink>
              <ButtonAsLink
                className={classNames(
                  'e-ProductFilterVariant__filterButtonFold',
                  {
                    'u-hidden': shouldCropFilters,
                  }
                )}
                onClick={() => this.setState({ shouldCropFilters: true })}
                name="product-filters-fold"
                inline
              >
                <div className="u-font-weight--bold u-font-weight-rebranding--medium">
                  <Trans id="searchResults.label.showLess" />
                </div>
              </ButtonAsLink>
              <ClearRefinementsSide
                filterId={FILTER_ID}
                disabled={!hasAnyFilterAdjusted}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }
}

ProductFilterVariant.propTypes = {
  i18n: i18nType.isRequired,
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  indexName: string.isRequired,
  searchState: shape({}),
  searchResults: shape({}),
  displayedFiltersNumber: number,
  searching: bool,
}

ProductFilterVariant.defaultProps = {
  searchState: null,
  searchResults: {},
  displayedFiltersNumber: 4,
  searching: false,
}

export default compose(
  connectStateResults,
  withI18n(),
  withShopAndRuntimeConfig
)(ProductFilterVariant)
